import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  ShieldCheck, Lock, DeviceMobile, Eye, DownloadSimple, Trash, FileText, ClipboardText, Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatusBadge, StatCard } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const TOGGLES = [
  ["listed_in_directory", "List me in the people directory"],
  ["show_email", "Show my email to other members"],
  ["show_phone", "Show my phone number"],
  ["show_company", "Show my company"],
];

export default function Privacy() {
  const { user, logout } = useAuth();
  const [view, setView] = useState("account");
  const [state, setState] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [consents, setConsents] = useState([]);
  const [audit, setAudit] = useState([]);
  const [overview, setOverview] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [challengeId, setChallengeId] = useState("");
  const [code, setCode] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const location = useLocation();
  const forcePassword = !!location.state?.forcePassword || !!user?.must_change_password;

  useEffect(() => {
    if (forcePassword) {
      setPw({ current_password: "", new_password: "" });
      setDialog("password");
    }
  }, [forcePassword]);

  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    try {
      const [pv, se, co] = await Promise.all([
        api.get("/security/privacy"),
        api.get("/security/sessions"),
        api.get("/security/consents"),
      ]);
      setState(pv.data); setSessions(se.data); setConsents(co.data);
      if (user?.role === "admin") {
        const [au, ov] = await Promise.all([
          api.get("/security/audit?limit=100"),
          api.get("/security/overview"),
        ]);
        setAudit(au.data); setOverview(ov.data);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    try {
      await fn();
      toast.success(ok);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setToggle = async (key, value) => {
    const next = { ...state.privacy, [key]: value };
    setState({ ...state, privacy: next });
    try {
      await api.patch("/security/privacy", next);
      toast.success("Privacy updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      load();
    }
  };

  const start2fa = async () => {
    try {
      const { data } = await api.post("/security/2fa/start");
      setChallengeId(data.challenge_id);
      setCode("");
      setDialog("2fa");
      toast.success(data.message);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const confirm2fa = async (e) => {
    e.preventDefault();
    try {
      await api.post("/security/2fa/enable", { challenge_id: challengeId, code });
      toast.success("Two-factor authentication is on");
      setDialog(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    try {
      await api.post("/security/password", pw);
      toast.success("Password changed — signing you out everywhere");
      setDialog(null);
      setTimeout(() => logout(), 1200);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const exportData = async () => {
    try {
      const res = await api.get("/security/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "my-data.json";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Your data is downloading");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteAccount = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/security/delete-account", { code: confirmPw });
      toast.success(`Scheduled — you have ${data.grace_days} days to change your mind`);
      setDialog(null);
      setTimeout(() => logout(), 1500);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const VIEWS = [
    ["account", "Account security"],
    ["privacy", "My privacy"],
    ["consents", `Consents (${consents.length})`],
    ...(isAdmin ? [["oversight", "Oversight"]] : []),
  ];

  const pending = state?.deactivation?.status === "pending";

  return (
    <div data-testid="privacy-page">
      <PageHeader
        overline="Trust"
        title="Privacy & security"
        description="Two-factor sign-in, the devices signed into your account, what other members can see about you, a copy of everything we hold on you, and a full audit trail of sensitive actions."
        testid="privacy-header"
      />

      <Tabs value={view} onValueChange={setView} className="mb-5">
        <TabsList className="w-full justify-start overflow-x-auto" data-testid="privacy-views">
          {VIEWS.map(([value, label]) => (
            <TabsTrigger key={value} value={value} data-testid={`sec-view-${value}`} className="whitespace-nowrap">
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* ---------------- Account security ---------------- */}
      {view === "account" && state && (
        <div className="space-y-4" data-testid="account-panel">
          {forcePassword && (
            <div data-testid="force-password-banner" className="border border-[#F6DCA0] bg-[#FFF8E6] px-4 py-3 text-sm text-[#8A6A00]">
              <span className="font-semibold">Choose your own password to continue.</span> This account
              is on a temporary password — set a new one and the rest of the workspace opens up.
            </div>
          )}

          <div className="flex flex-col gap-3 border border-border bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#0F172A]">
                <DeviceMobile size={16} /> Two-factor authentication
                <StatusBadge value={state.twofa_enabled ? "active" : "pending"} testid="twofa-status" />
              </div>
              <p className="mt-1 text-xs text-slate-500">
                We email a 6-digit code every time you sign in. Strongly recommended for admins,
                lenders, insurers and government officers.
              </p>
            </div>
            {state.twofa_enabled ? (
              <Button variant="outline" data-testid="disable-2fa-button" onClick={() => { setConfirmPw(""); setDialog("disable2fa"); }}>
                Turn off
              </Button>
            ) : (
              <Button data-testid="enable-2fa-button" onClick={start2fa}>Turn on</Button>
            )}
          </div>

          <div className="flex flex-col gap-3 border border-border bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#0F172A]">
                <Lock size={16} /> Password
              </div>
              <p className="mt-1 text-xs text-slate-500">
                At least 10 characters with upper and lower case, a number and a symbol.
                Changing it signs out every device.
              </p>
            </div>
            <Button variant="outline" data-testid="change-password-button" onClick={() => { setPw({ current_password: "", new_password: "" }); setDialog("password"); }}>
              Change password
            </Button>
          </div>

          <div className="border border-border bg-white p-4" data-testid="sessions-card">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm font-semibold text-[#0F172A]">Signed-in devices ({sessions.length})</div>
              <Button size="sm" variant="outline" data-testid="revoke-others-button" onClick={() => act(() => api.post("/security/sessions/revoke-others"), "Signed out everywhere else")}>
                Sign out everywhere else
              </Button>
            </div>
            <div className="mt-3 space-y-2">
              {sessions.length === 0 ? (
                <p className="text-xs text-slate-500">No sessions on record yet.</p>
              ) : sessions.map((s) => (
                <div key={s.id} data-testid={`session-${s.id}`} className="flex flex-col gap-2 bg-slate-50 p-2.5 text-xs sm:flex-row sm:items-center sm:justify-between">
                  <span className="min-w-0 break-words text-slate-600">
                    {s.current && <span className="mr-1 font-semibold text-[#002FA7]">This device ·</span>}
                    {s.ip || "unknown IP"} · {(s.user_agent || "unknown device").slice(0, 70)} · since {String(s.created_at).slice(0, 16).replace("T", " ")}
                  </span>
                  {!s.current && (
                    <Button size="sm" variant="ghost" data-testid={`revoke-session-${s.id}`} onClick={() => act(() => api.post(`/security/sessions/${s.id}/revoke`), "Session signed out")}>
                      Sign out
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ---------------- Privacy ---------------- */}
      {view === "privacy" && state && (
        <div className="space-y-4" data-testid="privacy-panel">
          <div className="border border-border bg-white p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-[#0F172A]">
              <Eye size={16} /> What others can see
            </div>
            <div className="mt-3 space-y-2">
              {TOGGLES.map(([key, label]) => (
                <label key={key} className="flex cursor-pointer items-center gap-2 text-xs text-slate-600">
                  <input
                    type="checkbox"
                    data-testid={`privacy-${key}`}
                    checked={!!state.privacy[key]}
                    onChange={(e) => setToggle(key, e.target.checked)}
                    className="h-4 w-4 accent-[#002FA7]"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-3 border border-border bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#0F172A]">
                <DownloadSimple size={16} /> Download my data
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Everything we hold that belongs to you — orders, shipments, policies, loans,
                claims, payments and consents — as one JSON file. The export is logged.
              </p>
            </div>
            <Button variant="outline" data-testid="export-data-button" onClick={exportData}>Export</Button>
          </div>

          <div className="flex flex-col gap-3 border border-[#F5C9C5] bg-[#FDECEA] p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#B3241B]">
                <Trash size={16} /> Delete my account
              </div>
              <p className="mt-1 text-xs text-[#B3241B]">
                {pending
                  ? `Scheduled for ${String(state.deactivation.purge_at).slice(0, 10)} — you can still cancel.`
                  : "We keep your account for 30 days first, so nothing is lost by accident."}
              </p>
            </div>
            {pending ? (
              <Button variant="outline" data-testid="cancel-deletion-button" onClick={() => act(() => api.post("/security/cancel-deletion"), "Deletion cancelled")}>
                Cancel deletion
              </Button>
            ) : (
              <Button variant="outline" data-testid="delete-account-button" onClick={() => { setConfirmPw(""); setDialog("delete"); }}>
                Delete account
              </Button>
            )}
          </div>
        </div>
      )}

      {/* ---------------- Consents ---------------- */}
      {view === "consents" && (
        <div className="space-y-3" data-testid="consents-panel">
          <div className="border border-border bg-white p-4 text-xs text-slate-600">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-[#0F172A]">
              <FileText size={16} /> Our policies
            </div>
            <div className="flex flex-wrap gap-3">
              <a href="/legal/privacy" data-testid="link-privacy-policy" className="font-semibold text-[#002FA7] underline">Privacy policy</a>
              <a href="/legal/terms" data-testid="link-terms" className="font-semibold text-[#002FA7] underline">Terms of use</a>
              <a href="/legal/cookies" data-testid="link-cookies" className="font-semibold text-[#002FA7] underline">Cookie notice</a>
            </div>
          </div>
          {consents.length === 0 ? (
            <EmptyState testid="no-consents" title="No consents recorded" description="Consents you give are logged here with the version and date." />
          ) : consents.map((c) => (
            <div key={c.id} data-testid={`consent-${c.id}`} className="flex flex-wrap items-center justify-between gap-2 border border-border bg-white p-3 text-xs text-slate-600">
              <span className="font-semibold text-[#0F172A]">{c.kind.replace("_", " ")}</span>
              <span>version {c.version} · {String(c.at).slice(0, 16).replace("T", " ")}{c.ip ? ` · ${c.ip}` : ""}</span>
            </div>
          ))}
        </div>
      )}

      {/* ---------------- Oversight (admin) ---------------- */}
      {view === "oversight" && isAdmin && (
        <div className="space-y-5" data-testid="oversight-panel">
          {overview && (
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
              <StatCard testid="kpi-2fa" label="2FA on" value={`${overview.twofa_on}/${overview.users}`} icon={ShieldCheck} />
              <StatCard testid="kpi-failed-logins" label="Failed logins 24h" value={overview.failed_logins_24h} tone={overview.failed_logins_24h > 0 ? "danger" : "success"} />
              <StatCard testid="kpi-locked" label="Locked accounts" value={overview.locked_accounts} tone={overview.locked_accounts > 0 ? "danger" : "default"} icon={Warning} />
              <StatCard testid="kpi-sessions" label="Active sessions" value={overview.active_sessions} />
              <StatCard testid="kpi-unverified" label="Unverified institutions" value={overview.unverified_insurers + overview.unverified_lenders} tone="primary" />
              <StatCard testid="kpi-audit" label="Audit events" value={overview.audit_events} icon={ClipboardText} />
            </div>
          )}
          {overview?.pending_deletions > 0 && (
            <div className="border border-[#F5C9C5] bg-[#FDECEA] p-3 text-xs text-[#B3241B]" data-testid="pending-deletions">
              {overview.pending_deletions} account(s) scheduled for deletion
            </div>
          )}
          <div className="space-y-2" data-testid="audit-panel">
            <div className="overline text-slate-500">Audit trail — most recent {audit.length}</div>
            {audit.length === 0 ? (
              <EmptyState testid="no-audit" title="Nothing logged yet" description="Sign-ins, role changes, exports, reveals and decisions all land here." />
            ) : audit.map((a) => (
              <div key={a.id} data-testid={`audit-${a.id}`} className="flex flex-col gap-1 border border-border bg-white p-3 text-xs text-slate-600 sm:flex-row sm:items-center sm:justify-between">
                <span className="min-w-0 break-words">
                  <span className="font-mono font-semibold text-[#002FA7]">{a.action}</span>
                  {" · "}{a.actor_name || "unknown"} ({a.actor_role || "—"})
                  {a.target ? ` · ${a.target}` : ""}
                  {a.ip ? ` · ${a.ip}` : ""}
                </span>
                <span className="shrink-0 text-slate-400">{String(a.at).slice(0, 19).replace("T", " ")}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---------------- Dialogs ---------------- */}
      <Dialog open={dialog === "2fa"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="twofa-dialog">
          <DialogHeader>
            <DialogTitle>Enter your code</DialogTitle>
            <DialogDescription>We emailed you a 6-digit code. It expires in 10 minutes.</DialogDescription>
          </DialogHeader>
          <form onSubmit={confirm2fa} className="space-y-3">
            <div>
              <Label>6-digit code</Label>
              <Input data-testid="twofa-code-input" inputMode="numeric" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value)} />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="submit-twofa-button" className="w-full sm:w-auto">Turn on 2FA</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "disable2fa"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="disable-twofa-dialog">
          <DialogHeader>
            <DialogTitle>Turn off two-factor</DialogTitle>
            <DialogDescription>Confirm with your password.</DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); act(() => api.post("/security/2fa/disable", { code: confirmPw }), "Two-factor turned off").then(() => setDialog(null)); }} className="space-y-3">
            <div>
              <Label>Password</Label>
              <Input data-testid="disable-twofa-password-input" type="password" required value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="submit-disable-twofa-button" className="w-full sm:w-auto">Turn off</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "password"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="password-dialog">
          <DialogHeader>
            <DialogTitle>Change password</DialogTitle>
            <DialogDescription>Every other device is signed out straight away.</DialogDescription>
          </DialogHeader>
          <form onSubmit={changePassword} className="space-y-3">
            <div>
              <Label>Current password</Label>
              <Input data-testid="current-password-input" type="password" required value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} />
            </div>
            <div>
              <Label>New password</Label>
              <Input data-testid="new-password-input" type="password" required value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} />
              <p className="mt-1 text-xs text-slate-500">10+ characters with upper and lower case, a number and a symbol.</p>
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="submit-password-button" className="w-full sm:w-auto">Change password</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "delete"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete my account</DialogTitle>
            <DialogDescription>We hold it for 30 days before anything is erased. Confirm with your password.</DialogDescription>
          </DialogHeader>
          <form onSubmit={deleteAccount} className="space-y-3">
            <div>
              <Label>Password</Label>
              <Input data-testid="delete-password-input" type="password" required value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="submit-delete-button" className="w-full sm:w-auto">Schedule deletion</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
